
import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import UpgradePrompt from './UpgradePrompt';
import TrialBanner from './TrialBanner';

interface FeatureGuardProps {
  children: React.ReactNode;
  featureName: string;
  requiresPremium?: boolean;
  showBanner?: boolean;
}

const FeatureGuard: React.FC<FeatureGuardProps> = ({
  children,
  featureName,
  requiresPremium = false,
  showBanner = false
}) => {
  const { canAccessFeature, trialStatus, startTrial, userTier } = useAuth();
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  const hasAccess = canAccessFeature(featureName);
  const isTier1 = userTier?.tier_level === 1;

  // Show banner for tier 1 users who haven't started trial or are in trial
  const shouldShowBanner = showBanner && isTier1 && (
  trialStatus.canStart || trialStatus.isActive || !trialStatus.isActive && trialStatus.hasStarted);

  if (!hasAccess) {
    return (
      <>
        {shouldShowBanner &&
        <TrialBanner
          daysRemaining={trialStatus.daysRemaining}
          isTrialActive={trialStatus.isActive}
          onStartTrial={startTrial}
          trialStarted={trialStatus.hasStarted} data-id="6p2x15sbn" data-path="src/components/trial/FeatureGuard.tsx" />

        }
        
        <div className="p-6 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 text-center" data-id="db94uev85" data-path="src/components/trial/FeatureGuard.tsx">
          <div className="mb-4" data-id="8fvqupi52" data-path="src/components/trial/FeatureGuard.tsx">
            <h3 className="text-lg font-semibold text-gray-700 mb-2" data-id="kgwucsyiy" data-path="src/components/trial/FeatureGuard.tsx">
              Premium Feature Required
            </h3>
            <p className="text-gray-600" data-id="jy9vy4dcg" data-path="src/components/trial/FeatureGuard.tsx">
              {trialStatus.hasStarted && !trialStatus.isActive ?
              `Your trial has expired. Upgrade to access "${featureName}".` :
              `Access to "${featureName}" requires ${requiresPremium ? 'a premium subscription' : 'starting your free trial'}.`
              }
            </p>
          </div>
          
          {trialStatus.canStart ?
          <button
            onClick={startTrial}
            className="px-6 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all" data-id="d2caynaq2" data-path="src/components/trial/FeatureGuard.tsx">

              Start 7-Day Free Trial
            </button> :

          <button
            onClick={() => setShowUpgradePrompt(true)}
            className="px-6 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all" data-id="w71rgg7oh" data-path="src/components/trial/FeatureGuard.tsx">

              Upgrade Now
            </button>
          }
        </div>

        <UpgradePrompt
          isOpen={showUpgradePrompt}
          onClose={() => setShowUpgradePrompt(false)}
          trialExpired={trialStatus.hasStarted && !trialStatus.isActive}
          featureName={featureName} data-id="90bvvjs3d" data-path="src/components/trial/FeatureGuard.tsx" />

      </>);

  }

  return (
    <>
      {shouldShowBanner &&
      <TrialBanner
        daysRemaining={trialStatus.daysRemaining}
        isTrialActive={trialStatus.isActive}
        onStartTrial={startTrial}
        trialStarted={trialStatus.hasStarted} data-id="kybsv0c96" data-path="src/components/trial/FeatureGuard.tsx" />

      }
      {children}
    </>);

};

export default FeatureGuard;